/**
 * Option lists for every institute field, mirroring the enums in the Prisma
 * schema. One source of truth — a screen never declares its own array of
 * boards or plans (CLAUDE.md §3).
 */

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

/** Helper so a select can render `{ value, label }` without extra mapping. */
function options<T extends string>(entries: Record<T, string>): Option<T>[] {
  return (Object.entries(entries) as [T, string][]).map(([value, label]) => ({ value, label }));
}

// --- institute profile ------------------------------------------------------

/**
 * What kind of place a customer is.
 *
 * The one answer half the product branches on: a college is asked for no board
 * and no teaching band, and admits students on basic details rather than into a
 * class and section.
 */
export const INSTITUTION_TYPE_LABEL = {
  // "School", not "Institute". Every customer is an institute — that is what
  // the product calls all of them — so labelling this one "Institute" made the
  // wizard ask "Kind of institute: Institute or College?" and would put a
  // meaningless badge on every row of the customer list.
  SCHOOL: 'School',
  COLLEGE: 'College',
} as const;
export type InstitutionType = keyof typeof INSTITUTION_TYPE_LABEL;
export const INSTITUTION_TYPE_OPTIONS = options(INSTITUTION_TYPE_LABEL);

export const BOARD_LABEL = {
  CBSE: 'CBSE',
  ICSE: 'ICSE / CISCE',
  STATE: 'State board',
  IB: 'IB',
  IGCSE: 'IGCSE / Cambridge',
  NIOS: 'NIOS',
  OTHER: 'Other',
} as const;
export type Board = keyof typeof BOARD_LABEL;
export const BOARD_OPTIONS = options(BOARD_LABEL);

/**
 * A board, or a dash when there is none.
 *
 * A college has no affiliation, so `board` is nullable now. One helper rather
 * than a `?? '—'` at each of the four places that print it, so they cannot
 * start disagreeing about what "none" looks like (CLAUDE.md §3).
 */
export function boardLabel(board: Board | null | undefined): string {
  return board ? BOARD_LABEL[board] : '—';
}

export const MANAGEMENT_LABEL = {
  GOVERNMENT: 'Government',
  AIDED: 'Government aided',
  PRIVATE_UNAIDED: 'Private unaided',
  MINORITY: 'Minority',
  CENTRAL: 'Central government',
} as const;
export type ManagementType = keyof typeof MANAGEMENT_LABEL;
export const MANAGEMENT_OPTIONS = options(MANAGEMENT_LABEL);

export const SCHOOL_LEVEL_LABEL = {
  PRE_PRIMARY: 'Pre-primary',
  PRIMARY: 'Primary (I–V)',
  UPPER_PRIMARY: 'Upper primary (VI–VIII)',
  SECONDARY: 'Secondary (IX–X)',
  SENIOR_SECONDARY: 'Senior secondary (XI–XII)',
  K12: 'K-12 (Nursery–XII)',
} as const;
export type SchoolLevel = keyof typeof SCHOOL_LEVEL_LABEL;
export const SCHOOL_LEVEL_OPTIONS = options(SCHOOL_LEVEL_LABEL);

/** The band an institute teaches, or a dash for a college, which teaches none. */
export function schoolLevelLabel(level: SchoolLevel | null | undefined): string {
  return level ? SCHOOL_LEVEL_LABEL[level] : '—';
}

/** A college's band. Its own list — the school bands say nothing true of a college. */
export const COLLEGE_LEVEL_LABEL = {
  INTERMEDIATE: 'Intermediate / Junior college (XI–XII)',
  UNDERGRADUATE: 'Undergraduate (degree)',
  POSTGRADUATE: 'Postgraduate',
  UG_PG: 'Undergraduate + Postgraduate',
} as const;
export type CollegeLevel = keyof typeof COLLEGE_LEVEL_LABEL;
export const COLLEGE_LEVEL_OPTIONS = options(COLLEGE_LEVEL_LABEL);

/**
 * The level to show for any institute — the college band for a college, the
 * school band otherwise. Branches on the type rather than on which column is
 * set, so a value left behind by a change of type is never shown.
 */
export function instituteLevelLabel(institute: {
  institutionType?: InstitutionType;
  schoolLevel?: SchoolLevel | null;
  collegeLevel?: CollegeLevel | null;
}): string {
  if (institute.institutionType === 'COLLEGE') {
    return institute.collegeLevel ? COLLEGE_LEVEL_LABEL[institute.collegeLevel] : '—';
  }
  return schoolLevelLabel(institute.schoolLevel);
}

export const GENDER_TYPE_LABEL = {
  BOYS: 'Boys only',
  GIRLS: 'Girls only',
  CO_ED: 'Co-educational',
} as const;
export type GenderType = keyof typeof GENDER_TYPE_LABEL;
export const GENDER_TYPE_OPTIONS = options(GENDER_TYPE_LABEL);

export const RESIDENTIAL_LABEL = {
  DAY: 'Day institute',
  BOARDING: 'Residential / boarding',
  DAY_BOARDING: 'Day + boarding',
} as const;
export type ResidentialType = keyof typeof RESIDENTIAL_LABEL;
export const RESIDENTIAL_OPTIONS = options(RESIDENTIAL_LABEL);

/** Class levels in order. Used for the lowest/highest class range. */
export const CLASS_LEVELS = [
  'NURSERY',
  'LKG',
  'UKG',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
] as const;
export type ClassLevel = (typeof CLASS_LEVELS)[number];
export const CLASS_LEVEL_OPTIONS: Option[] = CLASS_LEVELS.map((level) => ({
  value: level,
  label:
    level.charAt(0) +
    level
      .slice(1)
      .toLowerCase()
      .replace(/^(kg)$/, 'KG'),
}));

export const MEDIUM_OPTIONS: Option[] = [
  'English',
  'Hindi',
  'Marathi',
  'Telugu',
  'Tamil',
  'Kannada',
  'Malayalam',
  'Gujarati',
  'Bengali',
  'Punjabi',
  'Urdu',
  'Odia',
  'Assamese',
].map((language) => ({ value: language.toUpperCase(), label: language }));

export const STREAM_OPTIONS: Option[] = [
  { value: 'SCIENCE', label: 'Science' },
  { value: 'COMMERCE', label: 'Commerce' },
  { value: 'ARTS', label: 'Arts / Humanities' },
  { value: 'VOCATIONAL', label: 'Vocational' },
];

// --- lifecycle --------------------------------------------------------------

export const SCHOOL_STATUS_LABEL = {
  PENDING_SETUP: 'Pending setup',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  ARCHIVED: 'Archived',
} as const;
export type InstituteStatus = keyof typeof SCHOOL_STATUS_LABEL;
export const SCHOOL_STATUS_OPTIONS = options(SCHOOL_STATUS_LABEL);

/**
 * Whether an institute has paid the licence fee agreed for the software.
 *
 * Two answers only. Part payment is not one of them until there is a ledger to
 * hold the parts — a "partly paid" with no receipts behind it is a number
 * nobody can reconcile.
 */
export const PAYMENT_STATUS_LABEL = {
  UNPAID: 'Unpaid',
  PAID: 'Paid',
} as const;
export type PaymentStatus = keyof typeof PAYMENT_STATUS_LABEL;
export const PAYMENT_STATUS_OPTIONS = options(PAYMENT_STATUS_LABEL);

/** How an institute paid us for the licence. Mirrors `SubscriptionPaymentMethod`. */
export const SUBSCRIPTION_PAYMENT_METHOD_LABEL = {
  UPI: 'UPI',
  BANK_TRANSFER: 'Bank transfer',
  CHEQUE: 'Cheque',
  CASH: 'Cash',
  CARD: 'Card',
  OTHER: 'Other',
} as const;
export type SubscriptionPaymentMethod = keyof typeof SUBSCRIPTION_PAYMENT_METHOD_LABEL;
export const SUBSCRIPTION_PAYMENT_METHOD_OPTIONS = options(SUBSCRIPTION_PAYMENT_METHOD_LABEL);

/** Unpaid is a thing to chase, not a failure — amber rather than red. */
/**
 * Which Badge tone each state wears.
 *
 * Both maps are read only by the platform console, which is why the healthy
 * states are `info` — its own indigo — rather than the status green. A page
 * of customers is mostly healthy, so green was the loudest colour on it and it
 * belonged to no other part of the console. The two states that need somebody
 * to act keep their status colours: amber for a setup that has stalled, red
 * for an account that is stopped.
 *
 * `info` rather than `accent`: on a badge's tinted pill the brand accent only
 * reaches 3.3:1 in the dark theme, and `info` is the same indigo at a contrast
 * that passes AA.
 */
export const PAYMENT_STATUS_TONE = {
  UNPAID: 'warning',
  PAID: 'info',
} as const;

export const SCHOOL_STATUS_TONE = {
  PENDING_SETUP: 'warning',
  ACTIVE: 'info',
  SUSPENDED: 'critical',
  ARCHIVED: 'neutral',
} as const;

// --- operating settings -----------------------------------------------------
//
// An institute's own configuration, kept apart from its profile above because a
// different screen owns it and a different person changes it.

/** Days of the week, Monday first — the order a timetable is read in. */
export const WEEK_DAY_LABEL = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
} as const;
export type WeekDay = keyof typeof WEEK_DAY_LABEL;
export const WEEK_DAY_OPTIONS = options(WEEK_DAY_LABEL);

export const DATE_FORMAT_LABEL = {
  'DD/MM/YYYY': 'DD/MM/YYYY — 31/03/2027',
  'DD-MM-YYYY': 'DD-MM-YYYY — 31-03-2027',
  'YYYY-MM-DD': 'YYYY-MM-DD — 2027-03-31',
} as const;
export type DateFormat = keyof typeof DATE_FORMAT_LABEL;
export const DATE_FORMAT_OPTIONS = options(DATE_FORMAT_LABEL);

export const ATTENDANCE_MODE_LABEL = {
  DAILY: 'Once a day',
  PERIOD_WISE: 'Every period',
} as const;
export type AttendanceMode = keyof typeof ATTENDANCE_MODE_LABEL;
export const ATTENDANCE_MODE_OPTIONS = options(ATTENDANCE_MODE_LABEL);

export const GRADING_SYSTEM_LABEL = {
  MARKS: 'Marks out of a total',
  GRADE: 'Letter grades (A1, A2, B1…)',
  CGPA: 'CGPA',
  CCE: 'CCE / scholastic areas',
} as const;
export type GradingSystem = keyof typeof GRADING_SYSTEM_LABEL;
export const GRADING_SYSTEM_OPTIONS = options(GRADING_SYSTEM_LABEL);

// --- geography --------------------------------------------------------------

/** 28 states + 8 union territories. Validated against on both sides. */
export const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
] as const;

export type IndianState = (typeof INDIAN_STATES)[number];

export const STATE_OPTIONS: Option[] = INDIAN_STATES.map((state) => ({
  value: state,
  label: state,
}));

/** A person's gender and blood group — asked of students and staff alike. */
export const GENDER_LABEL = {
  MALE: 'Male',
  FEMALE: 'Female',
  OTHER: 'Other',
} as const;
export type StudentGender = keyof typeof GENDER_LABEL;
export const GENDER_OPTIONS = options(GENDER_LABEL);

/**
 * `UNKNOWN` is first and is the default: a blood group nobody has confirmed is
 * worse than none at all, because it is what a hospital would be told.
 */
export const BLOOD_GROUP_LABEL = {
  UNKNOWN: 'Not known',
  'A+': 'A+',
  'A-': 'A−',
  'B+': 'B+',
  'B-': 'B−',
  'O+': 'O+',
  'O-': 'O−',
  'AB+': 'AB+',
  'AB-': 'AB−',
} as const;
export type BloodGroup = keyof typeof BLOOD_GROUP_LABEL;
export const BLOOD_GROUP_OPTIONS = options(BLOOD_GROUP_LABEL);
